import type { ThemeConfig } from "@/types/theme";

export const tailwindFuchsia: ThemeConfig = {
  classes: {
    // Color classes
    primary: "text-fuchsia-700 dark:text-fuchsia-400",
    secondary: "text-fuchsia-900 dark:text-fuchsia-500",
    accent: "text-pink-600 dark:text-pink-400",

    // Background classes
    backgroundPrimary: "bg-white dark:bg-gray-950",
    backgroundSecondary: "bg-gray-50 dark:bg-gray-900",
    backgroundSurface: "bg-gray-50 dark:bg-gray-800",
    backgroundTertiary: "bg-fuchsia-50/60 dark:bg-gray-800",
    backgroundAccent: "bg-fuchsia-600 dark:bg-fuchsia-700",
    backgroundPrimaryHover: "hover:bg-gray-50 dark:hover:bg-gray-900",
    decorativeBackground:
      "bg-gradient-to-br from-fuchsia-50 to-pink-50 dark:from-gray-900 dark:to-gray-950",

    // Shared alternates (same across palettes)
    backgroundSoft: "bg-gray-50 dark:bg-gray-900",
    backgroundLighter: "bg-white dark:bg-gray-900",

    backgroundPattern: "bg-fuchsia-100 dark:bg-gray-800",
    backgroundPrimaryColor: "bg-fuchsia-600 dark:bg-fuchsia-500",
    backgroundSecondaryColor: "bg-fuchsia-800 dark:bg-fuchsia-700",
    backgroundAccentColor: "bg-pink-500 dark:bg-pink-400",
    backgroundPrimarySubtle: "bg-fuchsia-50 dark:bg-fuchsia-950",

    // Card backgrounds
    backgroundCard: "bg-white dark:bg-gray-950",
    backgroundElevated: "bg-white dark:bg-gray-800",
    backgroundHighlighted:
      "bg-gradient-to-br from-white to-fuchsia-50 dark:from-gray-800 dark:to-gray-900",

    // Text classes
    textPrimary: "text-gray-800 dark:text-gray-100",
    textSecondary: "text-gray-600 dark:text-gray-300",
    textMuted: "text-gray-500 dark:text-gray-400",
    textInverse: "text-white dark:text-gray-900",
    textAccent: "text-fuchsia-700 dark:text-fuchsia-400",
    textOnAccent: "text-white",

    // Text on dark backgrounds
    textPrimaryOnDark: "text-gray-100",
    textSecondaryOnDark: "text-gray-300",
    textMutedOnDark: "text-gray-400",

    // Icon classes
    iconText: "text-fuchsia-600 dark:text-fuchsia-400",
    iconBackgroundFilled: "bg-fuchsia-100 dark:bg-fuchsia-950",
    iconBackgroundGradient: "bg-gradient-to-br from-fuchsia-500 to-pink-500",
    iconBackgroundDefault: "bg-fuchsia-50 dark:bg-fuchsia-950/50",
    iconBorder: "border-fuchsia-200 dark:border-fuchsia-800",

    // Border classes
    borderDefault: "border-fuchsia-200 dark:border-gray-700",
    borderLight: "border-fuchsia-100 dark:border-gray-800",
    borderStrong: "border-fuchsia-300 dark:border-gray-600",
    borderPrimary: "border-fuchsia-700 dark:border-fuchsia-500",
    borderSecondary: "border-fuchsia-900 dark:border-fuchsia-700",
    borderBottom: "border-b border-fuchsia-200 dark:border-gray-700",
    borderAccent: "border-fuchsia-600 dark:border-fuchsia-400",

    // Badge classes
    badgeBackground: "bg-fuchsia-100 dark:bg-fuchsia-950",
    badgeText: "text-fuchsia-700 dark:text-fuchsia-300",

    // Shadow classes
    shadowPrimary: "shadow-fuchsia-500/20",
    shadowDefault: "shadow-sm",
    shadowHover: "hover:shadow-lg hover:shadow-fuchsia-500/10",
    shadowSmall: "shadow-sm",
    shadowMedium: "shadow-md",
    shadowLarge: "shadow-lg",
    shadowXLarge: "shadow-xl",
    shadowGlow: "shadow-[0_0_30px_rgba(217,70,239,0.30)]", // fuchsia-500 glow
    shadowInset: "shadow-inner",

    // Interactive states
    hoverBackground: "hover:bg-fuchsia-50 dark:hover:bg-gray-800",
    activeBackground: "active:bg-fuchsia-100 dark:active:bg-gray-700",
    accentPrimary:
      "bg-fuchsia-600 hover:bg-fuchsia-700 dark:bg-fuchsia-700 dark:hover:bg-fuchsia-600",

    // Creative Bold Hero classes
    backgroundBold: "bg-fuchsia-400 dark:bg-fuchsia-600",
    textEmphasis: "text-teal-600 dark:text-teal-400",

    // Gradient classes
    gradientPrimary: "bg-gradient-to-r from-fuchsia-600 to-fuchsia-700",
    gradientSecondary: "bg-gradient-to-br from-fuchsia-700 to-fuchsia-900",
    gradientAccent: "bg-gradient-to-r from-fuchsia-500 to-pink-500",
    gradientSubtle:
      "bg-gradient-to-br from-fuchsia-50 to-pink-50 dark:from-gray-900 dark:to-gray-950",
    gradientVibrant:
      "bg-gradient-to-r from-fuchsia-600 via-pink-500 to-fuchsia-600",
    gradientHero:
      "bg-gradient-to-br from-fuchsia-600 via-pink-500 to-fuchsia-700",

    // Border radius classes
    roundedSmall: "rounded",
    roundedMedium: "rounded-md",
    roundedLarge: "rounded-lg",
    roundedXLarge: "rounded-xl",
    roundedFull: "rounded-full",

    // Opacity classes
    opacityDisabled: "opacity-50",
    opacityHover: "opacity-80",
    opacityBackdrop: "opacity-60",
    opacitySubtle: "opacity-30",

    // Transition classes
    transitionFast: "transition-all duration-150",
    transitionNormal: "transition-all duration-300",
    transitionSlow: "transition-all duration-500",

    // Z-index classes
    zDropdown: "z-10",
    zModal: "z-50",
    zOverlay: "z-40",
    zTooltip: "z-[60]",
    zFixed: "z-30",

    // Status colors (kept consistent across palettes)
    error: "text-red-600 dark:text-red-400",
    success: "text-green-600 dark:text-green-400",
    warning: "text-amber-600 dark:text-amber-400",
    info: "text-blue-600 dark:text-blue-400",

    errorBg: "bg-red-100 dark:bg-red-950",
    successBg: "bg-green-100 dark:bg-green-950",
    warningBg: "bg-amber-100 dark:bg-amber-950",
    infoBg: "bg-blue-100 dark:bg-blue-950",
  },
};
